import { Home, BookOpen, Calendar, TrendingUp, Upload, FileText, User, Sparkles, History } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation } from "react-router-dom";
import logo from "@/assets/classplan-logo.png";
import logoLight from "@/assets/classplan-logo-light.png";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { prefetchRoute } from "@/lib/prefetch";

const mainItems = [
  { title: "Dashboard", url: "/dashboard", icon: Home, id: "nav-dashboard" },
  { title: "Ace", url: "/ace", icon: Sparkles, id: "nav-ace" },
  { title: "Courses", url: "/courses", icon: BookOpen, id: "nav-courses" },
  { title: "Calendar", url: "/calendar", icon: Calendar, id: "nav-calendar" },
  { title: "Grades", url: "/grades", icon: TrendingUp, id: "nav-grades" },
  { title: "Materials", url: "/materials", icon: FileText, id: "nav-materials" },
];

const actionItems = [
  { title: "Upload Syllabi", url: "/upload", icon: Upload },
  { title: "Job History", url: "/jobs", icon: History },
];

const accountItems = [
  { title: "My Account", url: "/account", icon: User },
];

export function AppSidebar() {
  const { open } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return currentPath === path;
    }
    return currentPath.startsWith(path);
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border/40">
      <SidebarContent className="bg-background">
        {/* Logo */}
        <div className="p-4 pb-6">
          <div className="flex items-center gap-3">
            <img src={logo} alt="ClassPlan Logo" className="h-7 w-7 flex-shrink-0 dark:hidden" loading="eager" />
            <img src={logoLight} alt="ClassPlan Logo" className="h-7 w-7 flex-shrink-0 hidden dark:block" loading="eager" />
            {open && <span className="font-semibold text-base tracking-tight">ClassPlan</span>}
          </div>
        </div>

        {/* Main Navigation */}
        <SidebarGroup className="px-2">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title} id={item.id}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={isActive(item.url)}
                    className={cn(
                      "relative rounded-lg transition-all duration-200",
                      isActive(item.url) 
                        ? "bg-accent text-foreground font-medium before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-0.5 before:bg-primary before:rounded-full" 
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    <NavLink 
                      to={item.url} 
                      className="gap-3"
                      onMouseEnter={() => prefetchRoute(item.url)}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Divider */}
        <div className="mx-4 my-3 border-t border-border/40" />

        {/* Actions */}
        <SidebarGroup className="px-2">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {actionItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={isActive(item.url)}
                    className={cn(
                      "relative rounded-lg transition-all duration-200",
                      isActive(item.url) 
                        ? "bg-accent text-foreground font-medium before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-0.5 before:bg-primary before:rounded-full" 
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    <NavLink 
                      to={item.url} 
                      className="gap-3"
                      onMouseEnter={() => prefetchRoute(item.url)}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Account */}
        <SidebarGroup className="px-2 pb-4">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {accountItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={isActive(item.url)}
                    className={cn(
                      "relative rounded-lg transition-all duration-200",
                      isActive(item.url) 
                        ? "bg-accent text-foreground font-medium before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-0.5 before:bg-primary before:rounded-full" 
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    <NavLink 
                      to={item.url} 
                      className="gap-3"
                      onMouseEnter={() => prefetchRoute(item.url)}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}